import React, { useState, useEffect } from 'react';
import { dbUTCDatetimeToLocal, commandToText } from '../../utils';
import {
  Divider,
  Grid,
  Header,
  Segment,
  Table,
  Button,
  Modal,
  Checkbox
} from 'semantic-ui-react';
import { servicesAPI } from '../../_services';
import { setToast } from '../../store/ToastManagement';
import { useCurrentUser, useSession, usePhalanx } from '../../_services';
import { SessionChart } from '../../_components/chart/SessionChart';
import Moment from 'moment-timezone';
import { extendMoment } from 'moment-range';

const moment = extendMoment(Moment);

export const DisplaySession = ({ dispatch, history, location }) => {
  const sessionId = location.pathname.split('/')[3];

  const { currentUser, currentUserCan } = useCurrentUser();

  const [readingData, setReadingData] = useState([]);
  const [sessionletData, setSessionletData] = useState([]);
  const [sessionData, setSessionData] = useState(null);

  const [currents, setCurrents] = useState(0);
  // const [energies, setEnergies] = useState(0);
  const [hydra, setHydra] = useState(null);
  const [tariffs, setTariffs] = useState([]);
  const [customerName, setCustomerName] = useState('\\_(ツ)_/');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sessionChart, setSessionChart] = useState('Loading');
  const [highlightMissingReadings, setHighlightMissingReadings] = useState(
    false
  );

  async function getSessionData(hydra, session, clear_cache) {
    try {
      const isAXON = ['AXON', 'AON'].includes(session?.analytics?.project);
      const readings = await servicesAPI.MeteringServices(
        'getSessionReadings',
        session.id,
        clear_cache,
        isAXON
      );
      let json = {};
      json[session.id] = {
        project: session?.analytics?.project,
        readings: readings,
        arbitration: session.analytics.charge_profiles
          ? session.analytics.charge_profiles
          : []
      };

      if (readings.length) {
        const start = readings[0];
        const stop = readings[readings.length - 1];

        let startReading = `${start.l1_energy} / ${start.l2_energy} / ${start.l3_energy}`;
        let stopReading = `${stop.l1_energy} / ${stop.l2_energy} / ${stop.l3_energy}`;

        // Highlight zero start reading in red
        if (start.l1_energy + start.l2_energy + start.l3_energy === 0)
          startReading = (
            <p style={{ color: 'red' }}>{startReading} (zero reading)</p>
          );
        // Highlight stop reading less than start reading in red
        if (
          stop.l1_energy + stop.l2_energy + stop.l3_energy <
          start.l1_energy + start.l2_energy + start.l3_energy
        )
          stopReading = (
            <p style={{ color: 'red' }}>
              {stopReading} (stop reading before start reading)
            </p>
          );

        setReadingData([
          {
            property: <b>Reading</b>,
            value: <b>Phases (L1 / L2 / L3)</b>
          },
          {
            property: 'Start reading',
            value: startReading
          },
          {
            property: 'Stop reading',
            value: stopReading
          }
        ]);
      }

      // STEP or SmartSTEP?
      let totalCurrent = 0;
      // let totalEnergy = 0;
      readings.forEach(r => {
        totalCurrent += r.l1_current + r.l2_current + r.l3_current;
        // totalEnergy += r.l1_energy + r.l2_energy + r.l3_energy;
      });

      setCurrents(totalCurrent !== 0);
      // setEnergies(totalEnergy !== 0);
      setSessionData(json);
    } catch (error) {}
  }

  const session = useSession(sessionId);
  const phalanx = usePhalanx(session ? session.phalanx_tag_id : null);

  const sessionStart = session ? dbUTCDatetimeToLocal(session.timestamp) : '-';
  const sessionEnd =
    session && session.stop_timestamp
      ? dbUTCDatetimeToLocal(session.stop_timestamp)
      : '-';

  useEffect(() => {
    servicesAPI.BillingServices('getTariffs').then(
      tariffs => setTariffs(tariffs),
      error => {}
    );
  }, []);

  useEffect(() => {
    if (!session || !hydra) return;

    getSessionData(hydra, session, false);

    // Get customer
    const values = {
      customer_to_lance_ids: [session.lance_customer_id]
    };

    servicesAPI
      .CustomerServices('getCustomerNamesFromCustomerToLances', values)
      .then(customers => {
        if (customers.length)
          setCustomerName(customers[0].forename + ' ' + customers[0].surname);
      });
  }, [session, hydra]);

  useEffect(() => {
    if (!sessionData || !currents) return;

    setSessionChart(
      <SessionChart
        sessions={sessionData}
        currents={currents}
        highlightMissingReadings={highlightMissingReadings}
      />
    );
  }, [sessionData, currents, highlightMissingReadings]);

  useEffect(() => {
    if (phalanx) {
      servicesAPI
        .InventoryServices('getHydra', phalanx.hydra_tag_id, false)
        .then(
          hydra => setHydra(hydra),
          error => {}
        );
    }
  }, [phalanx]);

  useEffect(() => {
    if (!phalanx) return;

    servicesAPI
      .MeteringServices('getSessionletReadings', sessionId)
      .then(sessionlets => {
        setSessionletData(
          sessionlets.map(s => {
            const start = s.start_datetime
              ? dbUTCDatetimeToLocal(s.start_datetime)
              : '';
            const end = s.stop_datetime
              ? dbUTCDatetimeToLocal(s.stop_datetime)
              : '';

            const consumption =
              s.er_l1_energy +
              s.er_l2_energy +
              s.er_l3_energy -
              (s.sr_l1_energy + s.sr_l2_energy + s.sr_l3_energy);

            return {
              ...s,
              start: start,
              end: end,
              consumption: consumption
            };
          })
        );
      });
  }, [phalanx]);

  const getTariffInfo = id => {
    const tariff = tariffs.find(t => t.id === id);
    return tariff ? `${tariff.name} ${tariff.cost}p` : id;
  };

  return (
    <Segment>
      <Grid stretched>
        <Grid.Row>
          <Grid.Column width={12}>
            <Header
              as="h2"
              onClick={() => {
                const bits = phalanx.reference.split('_cs');
                const hubRef = bits[0];
                const csId = 'cs' + bits[1];

                history.push(`/admin/hubs/${hubRef}/${csId}`);
              }}
            >
              Charging Session: {hydra ? hydra.description : '?'} CS
              {phalanx ? phalanx.reference.slice(-2) : '??'}
              {' (customer '}
              {customerName})
              <br />
            </Header>
            <p>
              Session duration: {sessionStart} to {sessionEnd}
            </p>
          </Grid.Column>
        </Grid.Row>
      </Grid>
      <Grid stretched>
        <Grid.Row>
          <Grid.Column>{sessionChart}</Grid.Column>
        </Grid.Row>
      </Grid>
      <Grid stretched>
        <Grid.Row>
          <Grid.Column width={13}>
            <Checkbox
              toggle
              label="Show missing readings"
              checked={highlightMissingReadings}
              onChange={e => {
                setHighlightMissingReadings(!highlightMissingReadings);
              }}
            />
          </Grid.Column>
          <Grid.Column width={3}>
            <Button
              color="olive"
              onClick={() => {
                getSessionData(hydra, session, true);
              }}
            >
              Recalculate graph
            </Button>
            <br />
            <Button
              onClick={() => setIsModalOpen(true)}
              color="red"
              disabled={!currentUserCan('DoAllTheThings')}
            >
              End Charging Session
            </Button>
          </Grid.Column>
        </Grid.Row>
      </Grid>
      <Divider hidden />
      <Grid>
        <Table
          basic="very"
          selectable
          stackable
          renderBodyRow={({ property, value }, idx) => ({
            key: idx,
            cells: [
              {
                key: 'property',
                content: property
              },
              {
                key: 'value',
                content: value
              }
            ]
          })}
          tableData={readingData}
        />
        <Table
          basic="very"
          selectable
          stackable
          headerRow={[
            'Sessionlet Id',
            'Started',
            'Ended',
            'L1 Start/End',
            'L2 Start/End',
            'L3 Start/End',
            'Consumption (Wh)',
            'Tariff Id'
          ]}
          renderBodyRow={({
            id,
            start,
            end,
            consumption,
            sr_tariff_id,
            sr_l1_energy,
            sr_l2_energy,
            sr_l3_energy,
            er_l1_energy,
            er_l2_energy,
            er_l3_energy
          }) => ({
            key: id,
            cells: [
              {
                key: 'id',
                onClick: () => {
                  // history.push(`/admin/session/${id}`);
                },
                content: id
              },
              {
                key: 'start',
                onClick: () => {
                  // history.push(`/admin/session/${id}`);
                },
                content: start
              },
              {
                key: 'end',
                onClick: () => {
                  // history.push(`/admin/session/${id}`);
                },
                content: end
              },
              {
                key: 'l1',
                content: sr_l1_energy + ', ' + er_l1_energy
              },
              {
                key: 'l2',
                content: sr_l2_energy + ', ' + er_l2_energy
              },
              {
                key: 'l3',
                content: sr_l3_energy + ', ' + er_l3_energy
              },
              {
                key: 'consumption',
                content: consumption
              },
              {
                key: 'sr_tariff_id',
                onClick: () => {
                  // history.push(`/admin/session/${id}`);
                },
                content: getTariffInfo(sr_tariff_id)
              }
            ]
          })}
          tableData={sessionletData.length && sessionletData}
        />
      </Grid>
      <Modal open={isModalOpen} size="small" style={{ padding: '20px' }}>
        <Modal.Content>
          Are you sure you want to end this charging session?
          <br />
          <br />
          <br />
          <Button
            basic
            color="olive"
            onClick={() => {
              setIsModalOpen(false);

              const parts = phalanx.reference.split('_');
              const csId = parts[parts.length - 1];
              const where = `${hydra.description} CS${csId}`;
              const command = 'END_SESS_' + sessionId;

              const timestamp =
                moment.utc().format('YYYY-MM-DDTHH:mm:ss') + ',Z';

              const values = {
                csms_command: command + ',' + timestamp
              };

              servicesAPI
                .InventoryServices(
                  'updatePhalanxByReference',
                  values,
                  phalanx.reference
                )
                .then(
                  response => {
                    dispatch(
                      setToast({
                        title: `Command sent to HUB!`,
                        body: 'Check Google Cloud logs to confirm'
                      })
                    );
                    servicesAPI.AdminServices(
                      'auditUserActivity',
                      {
                        entity_type: 'SESSION',
                        entity_id: sessionId,
                        activity: 'SupportOperation',
                        notes: `${where}: ${commandToText(command)}`,
                        email_subject: '',
                        email_body: ''
                      },
                      currentUser.id
                    );
                  },
                  error => {}
                );
            }}
          >
            Yes
          </Button>
          <Button
            basic
            onClick={event => {
              setIsModalOpen(false);
            }}
          >
            No
          </Button>
        </Modal.Content>
      </Modal>
    </Segment>
  );
};
